import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import {
  appendAttachmentToFormData,
  concatUrl,
  getCustomConfig,
  getNewAbortHandler,
  handleAttachmentBatchResponses,
} from "src/util/http/httpHelper"
import { Attachment, AttachmentUpload } from "src/util/types/dataModels/attachment"
import { BatchErrors, ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  IssuesOnItem,
  IssuesOnItemAction,
  IssuesOnItemActionFilterParams,
  IssuesOnItemFilterParams,
  IssuesOnItemPayload,
} from "src/util/types/dataModels/issue"
import {
  IssueComment,
  IssueCommentFilterParams,
  IssueCommentPayload,
} from "src/util/types/dataModels/issueComment"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ISSUES,
} from "src/util/types/dataModels/user"

//region Issues On Item

//region Issues On Item Base

/**
 * Get the list of all Issues On Item.
 *
 * @param config custom configurations and filtering params
 */
export async function getIssuesOnItemList(
  config: HttpConfig<IssuesOnItemFilterParams> = {}
): Promise<ObjectList<IssuesOnItem>> {
  const entityURL = CSIurl.ISSUES_ON_ITEM
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create new Issue On Item.
 *
 * @param config custom configurations and filtering params
 * @param issue the new Issue On Item
 */
export async function postIssuesOnItem(
  config: HttpConfig = {},
  issue: IssuesOnItemPayload
): Promise<IssuesOnItem> {
  const url = CSIurl.ISSUES_ON_ITEM
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, issue, { signal, customConfig })

  return data
}

/**
 * Retrieve an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param issueId
 */
export async function getIssuesOnItem(
  config: HttpConfig = {},
  issueId: string
): Promise<IssuesOnItem> {
  const url = CSIurl.ISSUES_ON_ITEM_ID.replace("{id}", issueId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param issueId the id of the Issue On Item that should be updated
 * @param issue the updated Issue On Item
 */
export async function putIssuesOnItem(
  config: HttpConfig<IssuesOnItemFilterParams> = {},
  issueId: string,
  issue: PayloadEntity<IssuesOnItem>
): Promise<IssuesOnItem> {
  const url = CSIurl.ISSUES_ON_ITEM_ID.replace("{id}", issueId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, issue, { signal, customConfig })

  return data
}

/**
 * Partially update an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param issueId the id of the Issue On Item that should be partially updated
 * @param issue the updated Issue On Item
 */
export async function patchIssuesOnItem(
  config: HttpConfig = {},
  issueId: string,
  issue: Partial<PayloadEntity<IssuesOnItem>>
): Promise<IssuesOnItem> {
  const url = CSIurl.ISSUES_ON_ITEM_ID.replace("{id}", issueId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, issue, { signal, customConfig })

  return data
}

/**
 * Create Issues On Item in bulk
 *
 * @param config custom configurations and filtering params
 * @param issues
 */
export async function postIssuesOnItemBatch(
  config: HttpConfig = {},
  issues: IssuesOnItemPayload[]
): Promise<ObjectList<IssuesOnItem>> {
  const url = CSIurl.ISSUES_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, issues, { signal, customConfig })

  return data
}

/**
 * Partially update Issues On Item in bulk.
 * The primary key of the issues, which are being updated
 * must be included as part of the payload data.
 *
 * @param config custom configurations and filtering params
 * @param issues
 */
export async function patchIssuesOnItemBatch(
  config: HttpConfig = {},
  issues: Partial<IssuesOnItem>[]
): Promise<ObjectList<IssuesOnItem>> {
  const url = CSIurl.ISSUES_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, issues, { signal, customConfig })

  return data
}

//endregion

//region Issue On Item Action

/**
 * Get the list of all Actions of an Issue On Item
 *
 * @param config custom configurations and filtering params
 */
export async function getIssuesOnItemActionsList(
  config: HttpConfig<IssuesOnItemActionFilterParams> = {}
): Promise<ObjectList<IssuesOnItemAction>> {
  const entityURL = CSIurl.ISSUE_ON_ITEM_ACTIONS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Action of an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param action
 */
export async function postIssuesOnItemAction(
  config: HttpConfig = {},
  action: PayloadEntity<IssuesOnItemAction>
): Promise<IssuesOnItemAction> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, action, { signal, customConfig })

  return data
}

/**
 * Get an individual Action of an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param actionId
 */
export async function getIssuesOnItemAction(
  config: HttpConfig = {},
  actionId: string
): Promise<IssuesOnItemAction> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS_ID.replace("{id}", actionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Action of an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param actionId the id of the Action of an Issue On Item that should be updated
 * @param action the updated Action of an Issue On Item
 */
export async function putIssuesOnItemAction(
  config: HttpConfig<IssuesOnItemActionFilterParams> = {},
  actionId: string,
  action: PayloadEntity<IssuesOnItemAction>
): Promise<IssuesOnItemAction> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS_ID.replace("{id}", actionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, action, { signal, customConfig })

  return data
}

/**
 * Partially update an individual Action of an Issue On Item
 *
 * @param config custom configurations and filtering params
 * @param actionId the id of the Action of an Issue On Item that should be partially updated
 * @param action the updated Action of an Issue On Item
 */
export async function patchIssuesOnItemAction(
  config: HttpConfig<IssuesOnItemActionFilterParams> = {},
  actionId: string,
  action: Partial<PayloadEntity<IssuesOnItemAction>>
): Promise<IssuesOnItemAction> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS_ID.replace("{id}", actionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, action, { signal, customConfig })

  return data
}

/**
 * Create Actions for an Issue On Item in bulk
 *
 * @param config custom configurations and filtering params
 * @param actions
 */
export async function postIssuesOnItemActionsBatch(
  config: HttpConfig = {},
  actions: PayloadEntity<IssuesOnItemAction>[]
): Promise<IssuesOnItemAction[]> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, actions, { signal, customConfig })

  return data
}

/**
 * Partially update Actions for an Issue On Item in bulk.
 * The primary keys of the Actions, which are being updated
 * must be included as part of the payload data.
 *
 * @param config custom configurations and filtering params
 * @param actions
 */
export async function patchIssuesOnItemActionsBatch(
  config: HttpConfig = {},
  actions: Partial<IssuesOnItemAction>[]
): Promise<IssuesOnItemAction[]> {
  const url = CSIurl.ISSUE_ON_ITEM_ACTIONS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, actions, { signal, customConfig })

  return data
}

//endregion

//region Issue On Item Attachments

export async function getIssueOnItemAttachmentList(
  config: HttpConfig = {},
  issue_id: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_LIST.replace("{issue_id}", issue_id)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["issue_item"]
    return i
  })

  return data
}

export async function postIssueOnItemAttachment(
  config: HttpConfig = {},
  issue_id: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_LIST.replace("{issue_id}", issue_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["issue_item"]

  return data
}

export async function getIssueOnItemAttachment(
  config: HttpConfig = {},
  issue_id: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_ID.replace("{issue_id}", issue_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["issue_item"]

  return data
}

/**
 * Delete an IssueOnItem Attachment
 *
 * @param config custom configurations and filtering params
 * @param issueId ID of the Issue on Item where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function deleteIssueOnItemAttachment(
  config: HttpConfig = {},
  issueId: string,
  attachmentId: string
) {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_ID.replace("{issue_id}", issueId).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  return await csiAPI.delete(url, { signal, customConfig })
}

export async function getIssueOnItemAttachmentContent(
  config: HttpConfig = {},
  issue_id: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_ID_CONTENT.replace("{issue_id}", issue_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postIssueOnItemAttachmentBatch(
  config: HttpConfig = {},
  issue_id: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_BATCH.replace("{issue_id}", issue_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "issue_item")
}

//endregion

//region Issue on Item Comment

/**
 * Get the list of all Issue on Item Comments
 *
 * @param config custom configurations and filtering params
 */
export async function getIssueOnItemCommentList(
  config: HttpConfig<IssueCommentFilterParams> = {}
): Promise<ObjectList<IssueComment>> {
  const entityURL = CSIurl.ISSUES_ON_ITEM_COMMENTS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Issue on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param issueComment the new Issue Comment
 */
export async function postIssueOnItemComment(
  config: HttpConfig = {},
  issueComment: IssueCommentPayload
): Promise<IssueComment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, issueComment, { signal, customConfig })

  return data
}

/**
 * Get an Issue on Item Comment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId
 */
export async function getIssueOnItemComment(
  config: HttpConfig = {},
  commentId: string
): Promise<IssueComment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing Issue on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Issue Comment that should be updated
 * @param comment the updated Issue Comment
 */
export async function putIssueOnItemComment(
  config: HttpConfig<IssueCommentFilterParams> = {},
  commentId: string,
  comment: PayloadEntity<IssueComment>
): Promise<IssueComment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, comment, { signal, customConfig })

  return data
}

/**
 * Partially update an Issue on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Issue Comment that should be partially updated
 * @param comment the updated Issue Comment
 */
export async function patchIssueOnItemComment(
  config: HttpConfig = {},
  commentId: string,
  comment: Partial<PayloadEntity<IssueComment>>
): Promise<IssueComment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comment, { signal, customConfig })

  return data
}

/**
 * Create Issue on Item Comments in batch
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function postIssueOnItemCommentBatch(
  config: HttpConfig = {},
  comments: IssueCommentPayload[]
): Promise<ObjectList<IssueComment> | ObjectList<BatchErrors | IssueComment>> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comments, { signal, customConfig })

  return data
}

/**
 * Partially update Issue on Item Comments in batch
 *
 * The primary key of the issue comments, which are being updated
 * must be included as part of the payload data
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function patchIssueOnItemCommentBatch(
  config: HttpConfig = {},
  comments: Partial<IssueComment>[]
): Promise<ObjectList<IssueComment> | ObjectList<BatchErrors | IssueComment>> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comments, { signal, customConfig })

  return data
}

//endregion

//region Issue on Item Comment Attachment

/**
 * Get a list of all the Attachments of an Issue on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue Comment where the Attachments belong to
 */
export async function getIssueOnItemCommentAttachmentList(
  config: HttpConfig = {},
  commentId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS.replace("{comment_id}", commentId)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["issue_item_comment"]
    return i
  })

  return data
}

/**
 * Create a new Issue on Item Comment Attachment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue Comment where the Attachment will be added
 * @param attachment Attachment to be uploaded
 */
export async function postIssueOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS.replace("{comment_id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["issue_item_comment"]

  return data
}

/**
 * Get an Issue on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getIssueOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["issue_item_comment"]

  return data
}

/**
 * Delete an Issue on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue on Item Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function deleteIssueOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
) {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

/**
 * Get an Issue on Item Comment Attachment content by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getIssueOnItemCommentAttachmentContent(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS_ID_CONTENT.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

/**
 * Create Issue on Item Comment Attachments in batch
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Issue Comment where the Attachment belongs to
 * @param attachments Attachments to be uploaded
 */
export async function postIssueOnItemCommentAttachmentBatch(
  config: HttpConfig = {},
  commentId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS_BATCH.replace("{comment_id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "issue_item_comment")
}

//endregion

//endregion
